import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

export const WaitingRoom = ({ client, setIsActiveCall }) => {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [isAudioEnabled, setIsAudioEnabled] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initialize video and audio stream
    useEffect(() => {
        let isMounted = true;

        const initializeMedia = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                    audio: true
                });

                if (!isMounted) {
                    // Component unmounted before we got the stream
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }

                streamRef.current = stream;
                setIsLoading(false);
            } catch (err) {
                console.error('Error accessing media devices:', err);
                if (isMounted) {
                    setError('Unable to access camera or microphone. Please check your permissions.');
                    setIsLoading(false);
                }
            }
        };

        initializeMedia();

        // Cleanup function to stop tracks when component unmounts
        return () => {
            isMounted = false;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
        };
    }, []);

    // Attach stream to video element when both are available
    useEffect(() => {
        if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
        }
    });

    // Update stream when video/audio toggles change
    useEffect(() => {
        if (streamRef.current) {
            const videoTrack = streamRef.current.getVideoTracks()[0];
            const audioTrack = streamRef.current.getAudioTracks()[0];

            if (videoTrack) {
                videoTrack.enabled = isVideoEnabled;
            }
            if (audioTrack) {
                audioTrack.enabled = isAudioEnabled;
            }
        }
    }, [isVideoEnabled, isAudioEnabled]);

    const toggleVideo = () => {
        setIsVideoEnabled(prev => !prev);
    };

    const toggleAudio = () => {
        setIsAudioEnabled(prev => !prev);
    };

    const handleJoinSession = () => {
        // Stop the preview stream before joining the actual session
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        setIsActiveCall(true);
    };

    return (
        <div className="no-scrollbar absolute top-0 bottom-0 left-0 right-0 wrapper h-dvh w-full flex items-center justify-center m-auto flex-col px-4 md:px-12 bg-[#FFFDF2]">
            <div className="flex flex-col items-center gap-6 w-full max-w-2xl">
                {/* Title */}
                <h1 className="text-2xl font-semibold text-[#0A0D14]">Waiting room</h1>

                {/* Instruction text */}
                <p className="text-center text-[#475467] text-sm md:text-base">
                    Please check your camera and microphone before joining.<br />
                    Sessions can only be joined on a PC or tablet
                </p>

                {/* Video Preview Container */}
                <div className="relative w-full aspect-video bg-black rounded-lg overflow-hidden shadow-lg">
                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                            <div className="text-white">Loading camera...</div>
                        </div>
                    ) : error ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                            <div className="text-white text-center px-4">{error}</div>
                        </div>
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                disablePictureInPicture
                                controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                                className="w-full h-full object-cover"
                            />

                            {/* Control buttons overlay */}
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3">
                                {/* Camera Toggle */}
                                <button
                                    onClick={toggleVideo}
                                    className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${isVideoEnabled
                                        ? 'bg-gray-700 hover:bg-gray-600'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                    aria-label={isVideoEnabled ? "Turn off camera" : "Turn on camera"}
                                >
                                    {isVideoEnabled ? (
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                    )}
                                </button>

                                {/* Microphone Toggle */}
                                <button
                                    onClick={toggleAudio}
                                    className={`w-12 h-12 rounded-lg flex items-center justify-center transition-all ${isAudioEnabled
                                        ? 'bg-gray-700 hover:bg-gray-600'
                                        : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                    aria-label={isAudioEnabled ? "Mute microphone" : "Unmute microphone"}
                                >
                                    {isAudioEnabled ? (
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                        </svg>
                                    )}
                                </button>

                                {/* Background Filter/Virtual Background Button */}
                                {/* <button
                                    className="w-12 h-12 rounded-lg bg-gray-700 hover:bg-gray-600 flex items-center justify-center transition-all"
                                    aria-label="Background filter"
                                >
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <rect x="3" y="3" width="18" height="18" rx="1" strokeWidth={1.5} />
                                        <circle cx="12" cy="9" r="2.5" strokeWidth={1.8} />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 18c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5" />
                                        <line x1="5" y1="5" x2="9" y2="9" strokeWidth="1" opacity="0.6" />
                                        <line x1="7" y1="5" x2="11" y2="9" strokeWidth="1" opacity="0.6" />
                                        <line x1="9" y1="5" x2="13" y2="9" strokeWidth="1" opacity="0.6" />
                                        <line x1="11" y1="5" x2="15" y2="9" strokeWidth="1" opacity="0.6" />
                                        <line x1="13" y1="5" x2="17" y2="9" strokeWidth="1" opacity="0.6" />
                                        <line x1="15" y1="5" x2="19" y2="9" strokeWidth="1" opacity="0.6" />
                                    </svg>
                                </button> */}
                            </div>
                        </>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3 w-full justify-center">
                    <button
                        onClick={() => navigate("/dashboard/appointments")}
                        className="rounded-[50px] border border-[#E2E4E9] text-black text-sm py-[10px] font-semibold px-4 hover:bg-gray-50 transition-colors"
                    >
                        Go back to home
                    </button>

                    <button
                        onClick={handleJoinSession}
                        disabled={isLoading}
                        className="bg-[#1A6A73] rounded-[50px] border border-[#1A6A73] text-white text-sm py-[10px] font-semibold px-4 hover:bg-[#155a63] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Join session
                    </button>
                </div>
            </div>
        </div>
    );
};
